import type { Tagged } from 'type-fest/source/tagged';

import { ApiError } from './errors/ApiError';
import type { ApiHelpTopic } from './TeoApi';
import type { TeoCore } from './TeoCore';
import { TeoEventTarget } from './utility/TeoEventTarget';

type TeoHelpEvents = {
  helpChanged: (help: Promise<TeoHelpTopic> | null) => void;
};

export class TeoHelp extends TeoEventTarget<TeoHelpEvents> {
  private teoCore: TeoCore;

  current: Promise<TeoHelpTopic> | null = null;
  constructor(teoCore: TeoCore) {
    super();
    this.teoCore = teoCore;
  }

  open(props: { topic: string; section?: string; content?: string }) {
    console.log('open help', props);
    const resultPromise = this.teoCore.api.getHelpTopic(props.topic);
    this.current = new Promise((resolve) => {
      resultPromise.then((result) => {
        if (ApiError.matches(result)) {
          return resolve({
            id: props.topic,
            title: '',
            sections: [],
            error: (result as ApiError).message,
          });
        } else {
          return resolve(
            TeoHelp.fromApiHelpTopic(result as ApiHelpTopic, props.section, props.content),
          );
        }
      });
    });
    this.triggerHelpChanged();
  }

  close() {
    this.current = null;
    this.triggerHelpChanged();
  }

  private triggerHelpChanged() {
    console.log('triggerHelpChanged', this.current);
    this.triggerEvent('helpChanged', this.current);
  }

  static fromApiHelpTopic(
    apiTopic: ApiHelpTopic,
    selectedSection?: string,
    selectedContent?: string,
  ): TeoHelpTopic {
    return {
      id: apiTopic.key_word,
      title: apiTopic.title,
      sections: apiTopic.sections.map((section): TeoHelpSection => {
        const hasOnlyOneSub = section.sub_topics && section.sub_topics.length === 1;
        return {
          id: section.key_word,
          title: section.title,
          selected: section.key_word === selectedSection,
          content: [
            ...(section.sub_topics?.map(
              (sub): TeoHelpContent => ({
                id: sub.key_word,
                title: sub.title,
                selected: hasOnlyOneSub ? true : sub.key_word === selectedContent,
                content: sub.text as Markdown,
              }),
            ) ?? []),
          ],
        };
      }),
    };
  }
}

export type Markdown = Tagged<string, 'Markdown'>;

export interface TeoHelpTopic {
  id: string;
  title: string;
  sections: TeoHelpSection[];
  error?: unknown;
}

export interface TeoHelpSection {
  id: string;
  title: string;
  selected: boolean;
  content: TeoHelpContent[];
}

export interface TeoHelpContent {
  id: string;
  title: string;
  selected: boolean;
  content: Markdown;
}

// this.current = new Promise((resolve) => {
//   setTimeout(() => {
//     resolve({
//       id: 'perf-dim',
//       title: 'Performance Dimensionen',
//       sections: [
//         {
//           id: 's1',
//           title: 'Verständlichkeit',
//           selected: props.section === 's1',
//           content: [
//             {
//               id: 's1c1',
//               title: 'Was wird gemessen?',
//               selected: props.content === 's1c1',
//               content:
//                 `Gemessen werden **Wort- und Satzlängen**. Denn lange Wörter und Sätze erschweren die Verständlichkeit. Dazu werden Sätze mit **mehr als zwei Satzteilen** markiert. Sie enthalten oftmals eingeschobene Nebensätze. Durch die Markierungen erhalten Sie die Chance, die entsprechenden Text-Passagen noch einmal zu prüfen und zu optimieren.` as Markdown,
//             },
//             {
//               id: 's1c2',
//               title: 'Was wird markiert?',
//               selected: props.content === 's1c2',
//               content: `_Todo..._` as Markdown,
//             },
//             {
//               id: 's1c3',
//               title: 'Was sagen die Werte?',
//               selected: props.content === 's1c3',
//               content: `_Todo..._` as Markdown,
//             },
//             {
//               id: 's1c4',
//               title: 'Best Practices Beispiele',
//               selected: props.content === 's1c4',
//               content: `_Todo..._` as Markdown,
//             },
//           ],
//         },
//         {
//           id: 's2',
//           title: 'Bildhaftigkeit',
//           selected: props.section === 's2',
//           content: [
//             {
//               id: 's2c1',
//               title: 'Was wird gemessen?',
//               selected: props.content === 's2c1',
//               content:
//                 `Gemessen wird die Anzahl der Substantive (Hauptwörter). Denn zu viele Substantive im Text sind typisch für Behördensprache und wirken distanziert (Nominalstil). Dazu wird der Anteil der bildleeren, abstrakten Substantive analysiert. Und auch die sogenannten Substantivierungen werden ausgewiesen.` as Markdown,
//             },
//             {
//               id: 's2c2',
//               title: 'Was wird markiert?',
//               selected: props.content === 's2c2',
//               content: `_Todo..._` as Markdown,
//             },
//             {
//               id: 's2c3',
//               title: 'Was sagen die Werte?',
//               selected: props.content === 's2c3',
//               content: `_Todo..._` as Markdown,
//             },
//             {
//               id: 's2c4',
//               title: 'Best Practices Beispiele',
//               selected: props.content === 's2c4',
//               content: `_Todo..._` as Markdown,
//             },
//           ],
//         },
//         {
//           id: 's3',
//           title: 'Aktivierungs-Qualität',
//           selected: props.section === 's3',
//           content: [
//             {
//               id: 's3c1',
//               title: 'Was wird gemessen?',
//               selected: props.content === 's3c1',
//               content:
//                 `Gemessen werden Aufforderungen und Apelle an den Leser. Das dient zur Führung des Lesers (wenn etwas zu tun ist). Und es erhöht die Reaktionsbereitschaft, wenn eine Antwort oder Reaktion gewünscht ist.` as Markdown,
//             },
//             {
//               id: 's3c2',
//               title: 'Was wird markiert?',
//               selected: props.content === 's3c2',
//               content: `_Todo..._` as Markdown,
//             },
//             {
//               id: 's3c3',
//               title: 'Was sagen die Werte?',
//               selected: props.content === 's3c3',
//               content: `_Todo..._` as Markdown,
//             },
//             {
//               id: 's3c4',
//               title: 'Best Practices Beispiele',
//               selected: props.content === 's3c4',
//               content: `_Todo..._` as Markdown,
//             },
//           ],
//         },
//         {
//           id: 's4',
//           title: 'Persönliche Merkmale',
//           selected: props.section === 's4',
//           content: [
//             {
//               id: 's4c1',
//               title: 'Was wird gemessen?',
//               selected: props.content === 's4c1',
//               content:
//                 `Gemessen werden persönliche Wörter und persönliche Sätze. Denn die direkte Ansprache des Lesers verstärkt die Wirkung des Textes. Zusätzlich wird die Perspektive analysiert, aus der Sie schreiben.` as Markdown,
//             },
//             {
//               id: 's4c2',
//               title: 'Was wird markiert?',
//               selected: props.content === 's4c2',
//               content: `_Todo..._` as Markdown,
//             },
//             {
//               id: 's4c3',
//               title: 'Was sagen die Werte?',
//               selected: props.content === 's4c3',
//               content: `_Todo..._` as Markdown,
//             },
//             {
//               id: 's4c4',
//               title: 'Best Practices Beispiele',
//               selected: props.content === 's4c4',
//               content: `_Todo..._` as Markdown,
//             },
//           ],
//         },
//       ],
//     });
//   }, 500);
// });
